import { useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { BadgeCheck, ChevronDown, Undo2, Wallet, X } from 'lucide-react'
import { useMemo, useState } from 'react'
import { CommentToggle } from '@/components/invoices/CommentToggle'
import { IncomingInvoiceControls } from '@/components/invoices/IncomingInvoiceControls'
import { InvoiceNumberCell } from '@/components/invoices/InvoiceNumberCell'
import { AttentionStrip } from '@/components/ui/AttentionStrip'
import { PayoutCommentsPanel } from '@/components/invoices/PayoutCommentsPanel'
import { PayoutWaitingLabel } from '@/components/invoices/PayoutWaitingLabel'
import { EmptyState } from '@/components/ui/EmptyState'
import { ErrorState } from '@/components/ui/ErrorState'
import { Money } from '@/components/ui/Money'
import { PageHeader } from '@/components/ui/PageHeader'
import { ApproveIncomingInvoiceModal } from '@/components/invoices/ApproveIncomingInvoiceModal'
import { ReasonModal } from '@/components/ui/ReasonModal'
import { SegmentedControl } from '@/components/ui/SegmentedControl'
import { Skeleton } from '@/components/ui/Skeleton'
import { useIsAdmin } from '@/lib/auth'
import { toNumber } from '@/lib/decimal'
import { formatEuros } from '@/lib/format'
import { useSetPayout } from '@/lib/invoice-queries'
import type { PayoutBeneficiaryType, PayoutOverviewGroup, PayoutOverviewLine } from '@/lib/invoice-types'
import {
  useApproveIncomingInvoice,
  useDeclineIncomingInvoice,
  usePayoutOverview,
  useUndoDeclineIncomingInvoice,
} from '@/lib/payout-queries'
import { isPayable, isStuck, sortPayoutLines, summarize, waitingOn, type WaitingLine } from '@/lib/payout-waiting'
import { cn } from '@/lib/utils'
import { todayIsoDate } from '@/lib/week'

/**
 * De scope van de lijst. Twee doorsnedes vragen actie, gescheiden op wie er aan
 * zet is: bij "Factuur nodig" de ontvanger, bij "Uitbetalen" CW zelf. Ze sluiten
 * elkaar uit. Daarnaast alles wat openstaat (default) en "Alles" als de uitklap
 * naar de betaalde historie.
 */
type Scope = 'needs-invoice' | 'payable' | 'open' | 'all'

const TYPE_LABEL: Record<PayoutBeneficiaryType, string> = {
  person: 'Persoon',
  external: 'Extern',
}

/**
 * Vijf kolommen: het factuurnummer staat als derde regel onder klant en betreft,
 * zoals op het eigen uitbetalingen-scherm. Als eigen kolom kostte het de breedte
 * die de acties nodig hebben, en dan loopt een afgekeurde regel (intrekken plus
 * accorderen) over de inkoopfactuur-cel heen.
 */
const GRID = 'grid grid-cols-[minmax(0,1fr)_8rem_13.5rem_10rem_16rem] items-center gap-4 border-l-2 px-5'

const EMPTY: Record<Scope, { title: string; description: string }> = {
  'needs-invoice': {
    title: 'Niets blijft liggen',
    description: 'Geen enkele uitbetaling loopt vast op een inkoopfactuur.',
  },
  payable: {
    title: 'Niets uit te betalen',
    description: 'Geen enkele inkoopfactuur wacht op accordering of uitbetaling.',
  },
  open: {
    title: 'Niets openstaand',
    description: 'Alle uitbetalingen zijn afgerond. Kies "Alles" voor de historie.',
  },
  all: {
    title: 'Geen uitbetalingen',
    description: 'Er zijn nog geen verdeelregels om uit te betalen.',
  },
}

/** `null` betekent: geen doorsnede, toon de hele fetch. */
const FILTER: Record<Scope, ((line: WaitingLine) => boolean) | null> = {
  'needs-invoice': isStuck,
  payable: isPayable,
  open: null,
  all: null,
}

const TOTAL_LABEL: Record<Scope, string> = {
  'needs-invoice': 'Totaal vastgelopen',
  payable: 'Totaal uit te betalen',
  open: 'Totaal openstaand',
  all: 'Totaal openstaand',
}

interface ViewGroup extends PayoutOverviewGroup {
  attentionAmount: number
}

/** Filtert op de scope, sorteert de regels op urgentie en zet de drukste ontvangers bovenaan. */
function buildGroups(groups: PayoutOverviewGroup[], scope: Scope): ViewGroup[] {
  return groups
    .map((group) => {
      const filter = FILTER[scope]
      const lines = filter ? group.lines.filter(filter) : group.lines
      return { ...group, lines: sortPayoutLines(lines), attentionAmount: summarize(lines, isStuck).amount }
    })
    .filter((group) => group.lines.length > 0)
    .sort(
      (a, b) =>
        Number(b.attentionAmount > 0) - Number(a.attentionAmount > 0) ||
        b.attentionAmount - a.attentionAmount ||
        toNumber(b.openTotal) - toNumber(a.openTotal) ||
        a.name.localeCompare(b.name, 'nl'),
    )
}

const SCOPES: Scope[] = ['needs-invoice', 'payable', 'open', 'all']

export function PayoutsAdminPage() {
  const routeSearch = useSearch({ strict: false }) as { scope?: string }
  const navigate = useNavigate()
  const scope = routeSearch.scope && SCOPES.includes(routeSearch.scope as Scope) ? (routeSearch.scope as Scope) : 'open'
  function setScope(next: Scope) {
    navigate({
      to: '/uitbetalingen',
      search: (prev) => ({ ...prev, scope: next === 'open' ? undefined : next }),
      replace: true,
    })
  }

  const openQuery = usePayoutOverview('open')
  const allQuery = usePayoutOverview('all', scope === 'all')
  const activeQuery = scope === 'all' ? allQuery : openQuery

  const openLines = useMemo(() => (openQuery.data ?? []).flatMap((g) => g.lines), [openQuery.data])
  const openAttention = useMemo(() => summarize(openLines, isStuck), [openLines])
  const openPayable = useMemo(() => summarize(openLines, isPayable), [openLines])
  const allCount = allQuery.data?.reduce((n, g) => n + g.lines.length, 0)

  const groups = useMemo(() => buildGroups(activeQuery.data ?? [], scope), [activeQuery.data, scope])
  const visibleLines = useMemo(() => groups.flatMap((g) => g.lines), [groups])

  const openTotal = visibleLines.filter((l) => l.payoutStatus === 'OPEN').reduce((s, l) => s + toNumber(l.amount), 0)
  const paidTotal = visibleLines.filter((l) => l.payoutStatus === 'PAID').reduce((s, l) => s + toNumber(l.amount), 0)

  return (
    <div className="space-y-6">
      <PageHeader
        title="Alle uitbetalingen"
        description="Wat iedereen nog krijgt: inkoopfacturen accorderen en per ontvanger op uitbetaald zetten."
        actions={
          <SegmentedControl<Scope>
            label="Welke uitbetalingen"
            segments={[
              { value: 'needs-invoice', label: 'Factuur nodig', count: openAttention.count },
              { value: 'payable', label: 'Uitbetalen', count: openPayable.count },
              { value: 'open', label: 'Openstaand', count: openLines.length },
              { value: 'all', label: 'Alles', count: allCount },
            ]}
            value={scope}
            onChange={setScope}
          />
        }
      />

      {scope !== 'needs-invoice' && openAttention.count > 0 ? (
        <AttentionStrip
          amount={openAttention.amount}
          headline="loopt vast op een inkoopfactuur"
          description={`${openAttention.count === 1 ? '1 regel' : `${openAttention.count} regels`} waar de inkoopfactuur ontbreekt of is afgekeurd.`}
          action={
            <button
              type="button"
              className="btn-ghost h-8 whitespace-nowrap px-3 text-sm"
              onClick={() => setScope('needs-invoice')}
            >
              Bekijk deze regels
            </button>
          }
        />
      ) : null}

      {activeQuery.isError ? (
        <ErrorState error={activeQuery.error} onRetry={() => activeQuery.refetch()} />
      ) : activeQuery.isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="rounded-card bg-surface p-5 shadow-card">
              <div className="flex items-center justify-between">
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-5 w-24" />
              </div>
            </div>
          ))}
        </div>
      ) : groups.length === 0 ? (
        <EmptyState icon={Wallet} title={EMPTY[scope].title} description={EMPTY[scope].description} />
      ) : (
        <>
          <div className="space-y-3">
            {groups.map((group) => (
              <GroupCard key={group.key} group={group} />
            ))}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 rounded-card bg-fill-1 px-5 py-3">
            <span className="text-xs text-ink-faint">{TOTAL_LABEL[scope]}</span>
            <span className="flex items-center gap-5">
              {(scope === 'open' || scope === 'all') && openAttention.amount > 0 ? (
                <span className="text-xs text-status-overdue">
                  waarvan <span className="money font-medium">{formatEuros(openAttention.amount)}</span> vastgelopen
                </span>
              ) : null}
              {paidTotal > 0 ? (
                <span className="text-xs text-ink-faint">
                  al betaald <span className="money">{formatEuros(paidTotal)}</span>
                </span>
              ) : null}
              <span className="money text-sm font-semibold text-ink">{formatEuros(openTotal)}</span>
            </span>
          </div>
        </>
      )}
    </div>
  )
}

function GroupCard({ group }: { group: ViewGroup }) {
  const [open, setOpen] = useState(true)

  return (
    <div className="overflow-hidden rounded-card bg-surface shadow-card">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-3 px-5 py-3.5 text-left transition-colors hover:bg-fill-1"
      >
        <ChevronDown className={cn('h-4 w-4 shrink-0 text-ink-faint transition-transform', open ? '' : '-rotate-90')} />
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span className="truncate font-medium text-ink">{group.name}</span>
            <span className="label-caps rounded-md bg-fill-2 px-1.5 py-0.5 text-[10px] text-ink-faint">
              {TYPE_LABEL[group.type]}
            </span>
          </span>
          <span className="text-xs text-ink-faint">{group.lines.length === 1 ? '1 regel' : `${group.lines.length} regels`}</span>
        </span>
        <span className="shrink-0 text-right">
          <span className="money block text-sm font-semibold text-ink">{formatEuros(toNumber(group.openTotal))}</span>
          {group.attentionAmount > 0 ? (
            <span className="text-[11px] font-medium text-status-overdue">
              <span className="money">{formatEuros(group.attentionAmount)}</span> vastgelopen
            </span>
          ) : (
            <span className="label-caps text-[10px] text-ink-faint">openstaand</span>
          )}
        </span>
      </button>

      {open ? (
        <div className="border-t border-hairline">
          {group.type === 'person' && group.payoutDetails ? (
            <div className="flex flex-wrap items-baseline gap-x-5 gap-y-1 border-b border-hairline px-5 py-2.5 text-sm">
              <span className="label-caps text-ink-faint">Uitbetaalgegevens</span>
              {group.payoutDetails.iban || group.payoutDetails.accountHolderName ? (
                <>
                  {group.payoutDetails.accountHolderName ? <span className="text-ink-soft">{group.payoutDetails.accountHolderName}</span> : null}
                  {group.payoutDetails.iban ? <span className="font-mono text-ink-soft">{group.payoutDetails.iban}</span> : null}
                </>
              ) : (
                <span className="text-ink-faint">Nog niet ingevuld</span>
              )}
            </div>
          ) : null}
          <div className={cn(GRID, 'label-caps hidden border-b border-l-transparent border-b-hairline py-2 text-ink-faint xl:grid')}>
            <span>Klant · Betreft · Factuur</span>
            <span className="text-right">Bedrag</span>
            <span>Wacht op</span>
            <span>Inkoopfactuur</span>
            <span aria-hidden />
          </div>
          <div className="divide-y divide-hairline">
            {group.lines.map((line) => (
              <OverviewLineRow key={line.lineId} line={line} />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  )
}

function OverviewLineRow({ line }: { line: PayoutOverviewLine }) {
  const queryClient = useQueryClient()
  const isAdmin = useIsAdmin()
  const setPayout = useSetPayout(line.invoiceId)
  const approve = useApproveIncomingInvoice()
  const decline = useDeclineIncomingInvoice()
  const undoDecline = useUndoDeclineIncomingInvoice()
  const [declining, setDeclining] = useState(false)
  const [explaining, setExplaining] = useState(false)
  const [threadOpen, setThreadOpen] = useState(false)

  const difference = toNumber(line.incomingInvoiceDifference ?? '0')

  function startApprove() {
    setExplaining(true)
  }

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['payouts'] })
  const busy = setPayout.isPending || approve.isPending || decline.isPending || undoDecline.isPending
  const waiting = waitingOn(line, 'admin')

  function markPaid() {
    setPayout.mutate(
      { lineId: line.lineId, input: { payoutStatus: 'PAID', payoutPaidAt: todayIsoDate() } },
      { onSuccess: invalidate },
    )
  }

  function reopenPayout() {
    setPayout.mutate({ lineId: line.lineId, input: { payoutStatus: 'OPEN', payoutPaidAt: null } }, { onSuccess: invalidate })
  }

  const identity = (
    <span className="min-w-0">
      <span className="block truncate font-medium text-ink">{line.clientName}</span>
      <span className="block truncate text-sm text-ink-faint">{line.subject || '-'}</span>
      <InvoiceNumberCell
        invoiceId={line.invoiceId}
        invoiceNumber={line.invoiceNumber}
        canViewInvoice={line.canViewInvoice}
        variant="inline"
      />
    </span>
  )

  const controls = (
    <IncomingInvoiceControls
      lineId={line.lineId}
      stage={line.payoutStage}
      hasPdf={line.hasIncomingInvoicePdf}
      filename={line.incomingInvoiceFilename}
      incomingInvoiceNumber={line.incomingInvoiceNumber}
      share={line.amount}
      canManage={line.canManageFile}
      canView={line.canViewFile}
      autoSettle={line.autoSettle}
    />
  )

  const declineButton = (
    <button
      type="button"
      className="btn-ghost h-8 w-8 shrink-0 p-0 text-ink-faint"
      onClick={() => setDeclining(true)}
      disabled={busy}
      title="Inkoopfactuur afkeuren"
      aria-label="Inkoopfactuur afkeuren"
    >
      <X className="h-3.5 w-3.5" />
    </button>
  )

  const actions = (
    <>
      <CommentToggle
        count={line.commentCount}
        open={threadOpen}
        canComment={line.canComment}
        onToggle={() => setThreadOpen((o) => !o)}
      />
      {!isAdmin || line.autoSettle ? null : (
        <>
      {line.payoutStage === 'INGEDIEND' ? (
        <>
          {declineButton}
          <button
            type="button"
            className="btn-primary h-8 whitespace-nowrap px-3 text-sm"
            onClick={startApprove}
            disabled={busy}
          >
            <BadgeCheck className="h-3.5 w-3.5" />
            Accorderen
          </button>
        </>
      ) : null}
      {line.payoutStage === 'AFGEKEURD' ? (
        <>
          <button
            type="button"
            className="btn-ghost h-8 w-8 shrink-0 p-0 text-ink-faint"
            onClick={() => undoDecline.mutate(line.lineId)}
            disabled={busy}
            title="Afkeuring intrekken"
            aria-label="Afkeuring intrekken"
          >
            <Undo2 className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            className="btn-primary h-8 whitespace-nowrap px-3 text-sm"
            onClick={startApprove}
            disabled={busy}
          >
            <BadgeCheck className="h-3.5 w-3.5" />
            Accorderen
          </button>
        </>
      ) : null}
      {line.payoutStage === 'GEACCORDEERD' ? (
        <>
          {declineButton}
          <button
            type="button"
            className="btn-ghost h-8 w-8 shrink-0 p-0 text-ink-faint"
            onClick={() => approve.mutate({ lineId: line.lineId, approve: false })}
            disabled={busy}
            title="Accordering intrekken"
            aria-label="Accordering intrekken"
          >
            <Undo2 className="h-3.5 w-3.5" />
          </button>
          {/* Accorderen mag vooruitlopen op de klantbetaling, uitbetalen niet. */}
          {line.invoiceStatus === 'PAID' ? (
            <button type="button" className="btn-primary h-8 whitespace-nowrap px-3 text-sm" onClick={markPaid} disabled={busy}>
              Markeer betaald
            </button>
          ) : null}
        </>
      ) : null}
      {line.payoutStage === 'BETAALD' ? (
        <button
          type="button"
          className="btn-ghost h-8 w-8 shrink-0 p-0 text-ink-faint"
          onClick={reopenPayout}
          disabled={busy}
          title="Betaling intrekken"
          aria-label="Betaling intrekken"
        >
          <Undo2 className="h-3.5 w-3.5" />
        </button>
      ) : null}
        </>
      )}
    </>
  )

  return (
    <div className={cn('group', waiting.tone === 'muted' && 'opacity-60 transition-opacity hover:opacity-100')}>
      <div className={cn(GRID, waiting.border, 'hidden py-3.5 xl:grid')}>
        {identity}
        <Money
          amount={toNumber(line.amount)}
          secondaryAmount={line.incomingInvoiceTotal ? toNumber(line.incomingInvoiceTotal) : null}
          className="text-sm font-medium text-ink"
        />
        <PayoutWaitingLabel line={line} viewer="admin" />
        <span className="min-w-0">{controls}</span>
        <span className="flex items-center justify-end gap-1.5">{actions}</span>
      </div>

      <div className={cn('flex flex-col gap-2 border-l-2 px-5 py-3 xl:hidden', waiting.border)}>
        <div className="flex items-start justify-between gap-3">
          {identity}
          <Money
            amount={toNumber(line.amount)}
            secondaryAmount={line.incomingInvoiceTotal ? toNumber(line.incomingInvoiceTotal) : null}
            className="text-sm font-medium text-ink"
          />
        </div>
        <PayoutWaitingLabel line={line} viewer="admin" />
        <span className="min-w-0">{controls}</span>
        <span className="flex items-center justify-end gap-1.5">{actions}</span>
      </div>

      {threadOpen && (
        <div className="px-5 pb-3">
          <PayoutCommentsPanel lineId={line.lineId} canComment={line.canComment} />
        </div>
      )}

      {explaining && (
        <ApproveIncomingInvoiceModal
          lineId={line.lineId}
          canViewPdf={line.canViewFile}
          share={line.amount}
          invoiceNumber={line.incomingInvoiceNumber}
          invoicedAmount={line.incomingInvoiceAmount}
          invoicedTotal={line.incomingInvoiceTotal}
          difference={difference}
          reason={line.incomingInvoiceDifferenceReason}
          pending={approve.isPending}
          onClose={() => setExplaining(false)}
          onConfirm={() => approve.mutate({ lineId: line.lineId, approve: true }, { onSuccess: () => setExplaining(false) })}
          onDecline={() => {
            setExplaining(false)
            setDeclining(true)
          }}
        />
      )}
      {declining && (
        <ReasonModal
          title="Inkoopfactuur afkeuren"
          description="Een reden is verplicht. De ontvanger ziet hem bij zijn eigen uitbetaling en kan een gecorrigeerde factuur uploaden."
          confirmLabel="Afkeuren"
          placeholder="Waarom klopt deze factuur niet?"
          rows={4}
          danger
          pending={decline.isPending}
          onClose={() => setDeclining(false)}
          onConfirm={(reason) =>
            decline.mutate({ lineId: line.lineId, reason }, { onSuccess: () => setDeclining(false) })
          }
        />
      )}
    </div>
  )
}
